import { createSlice } from '@reduxjs/toolkit';
import { getScoreByAffinity,
    getDistributionByLocation,
    getDigitalVsNonDigitalAffinity,
    getDigitalChannelAffinity,
    getPersonalPromotionChannelAffinity,
    getPeerPeerAffinityData,
    getContentAffinityKeywords,
    getDynamicTopicCounts,
    getBrandedUnbrandedCounts,
    getDistributionByYearsOfExperience,
    getVendorAffinity,
    getRegionCount } from '../thunks/affinityHubThunk';
import { createMapSeries,
    createDigitalVsNonDigitalData, 
    generateChartSeries,
    generateChartRange,
    createPeerToPeerAffinityData,
    createTopicData,
    createContentTypeData,
    createDistributionYearExp,
    createTreeMapSeries,
    generateAffinityChartSeries,
    createSankeyChartSeries,
    transformChartSeriesForSankey,
    removeNotFound } from '../../utility';
import { PREFERENCE } from '../../constants';

const affinityHubSlice = createSlice({
    name: 'affinityHub',
    initialState: {
        isLoading: false,
        error: null,
        selectedState: [],
        selectedStates: [],
        selectedSpecialty: [],
        selectedSpecialties: [],
        selectedHcpType: [],
        selectedHcpTypes: [],
        affinity: {
            digital: {
                chartSeries: [],
                chartRange: []
            },
            personalPromotion: {
                chartSeries: [],
                chartRange: []
            },
            peerToPeer: {
                chartSeries: [],
                chartRange: []
            },
            data: null,
            isLoading: false,
            error: null
        },
        digitalVsNonDigital: {
            isLoading: false,
            error: null,
            seriesData: [],
            categories: []
        },
        digitalChannel: {
            isLoading: true,
            error: null,
            data: null,
            selectedPreference: 'All',
            chartSeries: [],
            categories: []
        },
        personalPromotionChannel: {
            isLoading: true,
            error: null,
            data: null,
            selectedPreference: 'All',
            chartSeries: [],
            categories: []
        },
        peerToPeer: {
            isLoading: true,
            error: null,
            virtualChannel: {
                labels: [],
                chartSeries: []
            },
            peerToPeerChannel: {
                labels: [],
                chartSeries: []
            },
            inPersonChannel: {
                labels: [],
                chartSeries: []
            }
        },
        vendorAffinity: {
            isLoading: false,
            error: null,
            data: null,
            chartSeries: [],
            categories: [],
            selectedChannel: '',
            vendors: []
        },
        contentAffinity: {
            isLoading: false,
            error: null,
            data: null,
            keyword1: {
                chartSeries: [],
                overallCount: 0
            },
            keyword2: {
                chartSeries: [],
                overallCount: 0
            },
            keyword3: {
                chartSeries: [],
                overallCount: 0
            },
            totalKeywords1Count: 0,
            totalKeywords2Count: 0,
            totalKeywords3Count: 0
        },
        topic: {
            isLoading: false,
            error: null,
            chartSeries: [],
            topRatedTopic: '',
            topRatedCount: 0
        },
        contentType: {
            isLoading: false,
            error: null,
            chartSeries: [],
            branded: 0,
            unbranded: 0
        },
        distributionByLocation: {
            isLoading: false,
            error: null,
            chartSeries: [],
            mapSeries: [],
            totalCount: 0,
            top15Total: 0
        },
        distributionByYearsofExp: {
            chartSeries: [],
            isLoading: false,
            error: null
        }
    },
    reducers: {
        setDigitalChannelPreference: (state, action) => {
            state.digitalChannel.selectedPreference = action.payload.preference;
            const preference = PREFERENCE[action.payload.preference];
            state.digitalChannel.chartSeries = state.digitalChannel.data[preference]?.seriesData || [];
            state.digitalChannel.categories = state.digitalChannel.data[preference]?.categories || [];
        },
        setPersonalPromotionChannelPreference: (state, action) => {
            state.personalPromotionChannel.selectedPreference = action.payload.preference;
            const preference = PREFERENCE[action.payload.preference];
            state.personalPromotionChannel.chartSeries = state.personalPromotionChannel.data[preference]?.seriesData || [];
            state.personalPromotionChannel.categories = state.personalPromotionChannel.data[preference]?.categories || [];
        },
        setVendorAffinityChannel: (state, action) => {
            const channelName = action.payload.channel;
            const channelData = state.vendorAffinity.data[action.payload.channel] || [];
            state.vendorAffinity.selectedChannel = channelName;
                    
            const { series, vendors } = transformChartSeriesForSankey(channelName, channelData);
            state.vendorAffinity.chartSeries = series;
            state.vendorAffinity.vendors = vendors;
        },
        handleSelectedFilterType: (state, action) => {
            const {
                type, option, actionType 
            } = action.payload;
            if(actionType === 'Add'){
                state[type === 'state' ? 'selectedState' : type === 'specialty' ? 'selectedSpecialty' : 'selectedHcpType'] = option;
                state[type === 'state' ? 'selectedStates' : type === 'specialty' ? 'selectedSpecialties' : 'selectedHcpTypes'].push(option);
            } else if (actionType === 'Remove') {
                state[type === 'state' ? 'selectedState' : type === 'specialty' ? 'selectedSpecialty' : 'selectedHcpType'] = [];
                state[type === 'state' ? 'selectedStates' : type === 'specialty' ? 'selectedSpecialties' : 'selectedHcpTypes'] =
                    state[type === 'state' ? 'selectedStates' : type === 'specialty' ? 'selectedSpecialties' : 'selectedHcpTypes'].
                        filter(optionItem => optionItem.label !== option.label);
            }
            
        }
        
        
        
        
    },
    extraReducers: (builder) => {
        builder
            /* Score By Affinity */
            .addCase(getScoreByAffinity.fulfilled, (state, action) => {
                state.affinity.error = null;
                state.affinity.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.affinity.data = action.payload;
                    /* Digital Affinity */ 
                    state.affinity.digital.chartSeries = generateChartSeries(action.payload.DG_score ?? {}, 'DG');
                    state.affinity.digital.chartRange = generateChartRange(action.payload.DG_score ?? {}, 'DG');
                    /* Personal Promotion Affinity */ 
                    state.affinity.personalPromotion.chartSeries = generateChartSeries(action.payload.PI_score ?? {}, 'PI');
                    state.affinity.personalPromotion.chartRange = generateChartRange(action.payload.PI_score ?? {}, 'PI');
                    /* Peer To Peer Affinity */ 
                    state.affinity.peerToPeer.chartSeries = generateChartSeries(action.payload.PP_score ?? {}, 'PP');
                    state.affinity.peerToPeer.chartRange = generateChartRange(action.payload.PP_score ?? {}, 'PP');
                }
            })
            .addCase(getScoreByAffinity.pending, (state, action) => {
                state.affinity.error = null;
                state.affinity.isLoading = true;
            })
            .addCase(getScoreByAffinity.rejected, (state, action) => {
                state.affinity.error = action.payload;
                state.affinity.isLoading = false;
            })
            /* Digital Vs Non Digital Affinity */ 
            .addCase(getDigitalVsNonDigitalAffinity.fulfilled, (state, action) => {
                state.digitalVsNonDigital.error = null;
                state.digitalVsNonDigital.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    const { categories, seriesData } = createDigitalVsNonDigitalData(action.payload || [], true);
                    state.digitalVsNonDigital.categories = categories;
                    state.digitalVsNonDigital.seriesData = seriesData;
                }    
                 
            })
            .addCase(getDigitalVsNonDigitalAffinity.pending, (state, action) => {
                state.digitalVsNonDigital.isLoading = true;
                state.digitalVsNonDigital.error = null;
            })
            .addCase(getDigitalVsNonDigitalAffinity.rejected, (state, action) => {
                state.digitalVsNonDigital.isLoading = false;
                state.digitalVsNonDigital.error = action.payload;
            })
            /* Digital Channel Affinity */ 
            .addCase(getDigitalChannelAffinity.fulfilled, (state, action) => {
                state.digitalChannel.error = null;
                state.digitalChannel.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    const result = generateAffinityChartSeries(action.payload[0], true);
                    state.digitalChannel.data = result;
                    const preference = PREFERENCE[state.digitalChannel.selectedPreference];
                    state.digitalChannel.chartSeries = result[preference]?.seriesData || [];
                    state.digitalChannel.categories = result[preference]?.categories || [];
                }    
                 
            })
            .addCase(getDigitalChannelAffinity.pending, (state, action) => {
                state.digitalChannel.isLoading = true;
                state.digitalChannel.error = null;
            })
            .addCase(getDigitalChannelAffinity.rejected, (state, action) => {
                state.digitalChannel.isLoading = false;
                state.digitalChannel.error = action.payload;
            })
            /* Personal Promotion Channel Affinity */ 
            .addCase(getPersonalPromotionChannelAffinity.fulfilled, (state, action) => {
                state.personalPromotionChannel.error = null;
                state.personalPromotionChannel.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    const result = generateAffinityChartSeries(action.payload[0], false);
                    state.personalPromotionChannel.data = result;
                    const preference = PREFERENCE[state.personalPromotionChannel.selectedPreference];
                    state.personalPromotionChannel.chartSeries = result[preference]?.seriesData || [];
                    state.personalPromotionChannel.categories = result[preference]?.categories || [];
                }    
                 
            })
            .addCase(getPersonalPromotionChannelAffinity.pending, (state, action) => {
                state.personalPromotionChannel.isLoading = true;
                state.personalPromotionChannel.error = null;
            })
            .addCase(getPersonalPromotionChannelAffinity.rejected, (state, action) => {
                state.personalPromotionChannel.isLoading = false;
                state.personalPromotionChannel.error = action.payload;
            })
            /* Distribution By Location */
            .addCase(getDistributionByLocation.fulfilled, (state, action) => {
                state.distributionByLocation.error = null;
                state.distributionByLocation.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.distributionByLocation.chartSeries = removeNotFound(action.payload || []);
                    state.distributionByLocation.totalCount = action.payload?.reduce((acc, curr) => acc += curr[1], 0) || 0;
                    state.distributionByLocation.top15Total = action.payload?.slice(0, 15)?.reduce((acc, curr) => acc += curr[1], 0) || 0;
                }
            })
            .addCase(getDistributionByLocation.pending, (state, action) => {
                state.distributionByLocation.isLoading = true;
                state.distributionByLocation.error = null;
            })
            .addCase(getDistributionByLocation.rejected, (state, action) => {
                state.distributionByLocation.isLoading = false;
                state.distributionByLocation.error = action.payload;
            })
            .addCase(getRegionCount.fulfilled, (state, action) => {
                state.distributionByLocation.error = null;
                state.distributionByLocation.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.distributionByLocation.mapSeries = createMapSeries(action.payload || []);
                }
            })
            .addCase(getRegionCount.pending, (state, action) => {
                state.distributionByLocation.isLoading = true;
                state.distributionByLocation.error = null;
            })
            .addCase(getRegionCount.rejected, (state, action) => {
                state.distributionByLocation.isLoading = false;
                state.distributionByLocation.error = action.payload;
            })
            /* Peer to Peer */ 
            .addCase(getPeerPeerAffinityData.fulfilled, (state, action) => {
                state.peerToPeer.error = null;
                state.peerToPeer.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.peerToPeer.virtualChannel = createPeerToPeerAffinityData(action.payload?.data?.filter(item => item?.channel === 'Virtual') || []);
                    state.peerToPeer.inPersonChannel = createPeerToPeerAffinityData(action.payload?.data?.filter(item => item?.channel === 'InPerson') || []);
                    state.peerToPeer.peerToPeerChannel = createPeerToPeerAffinityData(action.payload?.pp_compare_affinity || [], false);
                }    
                 
            })
            .addCase(getPeerPeerAffinityData.pending, (state, action) => {
                state.peerToPeer.isLoading = true;
                state.peerToPeer.error = null;
            })
            .addCase(getPeerPeerAffinityData.rejected, (state, action) => {
                state.peerToPeer.isLoading = false;
                state.peerToPeer.error = action.payload;
            })
            /* Content Affinity */ 
            .addCase(getContentAffinityKeywords.fulfilled, (state, action) => {
                state.contentAffinity.error = null;
                state.contentAffinity.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.contentAffinity.data = action.payload;
                    state.contentAffinity.keyword1.chartSeries = createTreeMapSeries(action.payload.keywords.keywords1, 'Keyword 1');
                    state.contentAffinity.totalKeywords1Count = action.payload.total_keywords1_count ?? 0;
                    state.contentAffinity.keyword2.chartSeries = createTreeMapSeries(action.payload.keywords.keywords2, 'Keyword 2');
                    state.contentAffinity.totalKeywords2Count = action.payload.total_keywords2_count ?? 0;
                    state.contentAffinity.keyword3.chartSeries = createTreeMapSeries(action.payload.keywords.keywords3, 'Keyword 3');
                    state.contentAffinity.totalKeywords3Count = action.payload.total_keywords3_count ?? 0;
                }    
                 
            })
            .addCase(getContentAffinityKeywords.pending, (state, action) => {
                state.contentAffinity.isLoading = true;
                state.contentAffinity.error = null;
            })
            .addCase(getContentAffinityKeywords.rejected, (state, action) => {
                state.contentAffinity.isLoading = false;
                state.contentAffinity.error = action.payload;
            })
            /* Topic */ 
            .addCase(getDynamicTopicCounts.fulfilled, (state, action) => {
                state.topic.error = null;
                state.topic.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.topic.chartSeries = createTopicData(action.payload.data || []);
                    state.topic.topRatedCount = action.payload.top_rated_count?.toFixed(2) || 0;
                    state.topic.topRatedTopic = action.payload.top_rated_topic || '';
                }    
                 
            })
            .addCase(getDynamicTopicCounts.pending, (state, action) => {
                state.topic.isLoading = true;
                state.topic.error = null;
            })
            .addCase(getDynamicTopicCounts.rejected, (state, action) => {
                state.topic.isLoading = false;
                state.topic.error = action.payload;
            })
            /* Content Type */ 
            .addCase(getBrandedUnbrandedCounts.fulfilled, (state, action) => {
                state.contentType.error = null;
                state.contentType.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.contentType.chartSeries = createContentTypeData(action.payload.data || []);
                    state.contentType.branded = action.payload.branded || 0;
                    state.contentType.unbranded = action.payload.unbranded || 0;
                }    
                 
            })
            .addCase(getBrandedUnbrandedCounts.pending, (state, action) => {
                state.contentType.isLoading = true;
                state.contentType.error = null;
            })
            .addCase(getBrandedUnbrandedCounts.rejected, (state, action) => {
                state.contentType.isLoading = false;
                state.contentType.error = action.payload;
            })
            /* Distribution By Years Of Experience */ 
            .addCase(getDistributionByYearsOfExperience.fulfilled, (state, action) => {
                state.error = null;
                state.distributionByYearsofExp.isLoading = false;
                const keys = Object.keys(action.payload || {});

                if(keys.includes('data')){
                    state.distributionByYearsofExp.chartSeries = createDistributionYearExp(action.payload || []);
                } else{
                    state.distributionByYearsofExp.chartSeries = [];
                }
            })
            .addCase(getDistributionByYearsOfExperience.pending, (state, action) => {
                state.distributionByYearsofExp.isLoading = true;
                state.distributionByYearsofExp.error = null;
            })
            .addCase(getDistributionByYearsOfExperience.rejected, (state, action) => {
                state.distributionByYearsofExp.isLoading = false;
                state.distributionByYearsofExp.error = action.payload;
            })
            /* Vendor Affinity */ 
            .addCase(getVendorAffinity.fulfilled, (state, action) => {
                state.vendorAffinity.error = null;
                state.vendorAffinity.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    const { categories, chartSeries } = createSankeyChartSeries(action.payload);
                    state.vendorAffinity.data = chartSeries;
                    state.vendorAffinity.categories = categories;
                    state.vendorAffinity.selectedChannel = categories[0];
                    const { series, vendors } = transformChartSeriesForSankey(categories[0], chartSeries[categories[0]]);
                    state.vendorAffinity.chartSeries = series;
                    state.vendorAffinity.vendors = vendors;
                    
                }
            })
            .addCase(getVendorAffinity.pending, (state, action) => {
                state.vendorAffinity.isLoading = true;
                state.vendorAffinity.error = null;
            })
            .addCase(getVendorAffinity.rejected, (state, action) => {
                state.vendorAffinity.isLoading = false;
                state.vendorAffinity.error = action.payload;
            });
    }
});

export const { actions } = affinityHubSlice;
export default affinityHubSlice.reducer;
